import { useCallback, useState } from 'react';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { isNil, omitBy } from 'lodash';
import { Button, DocumentUpload, Typography } from 'components';
import HazardIcon from 'assets/icons/hazard.svg?react';
import InfoIcon from 'assets/icons/info.svg?react';
import { useDocumentUpload } from 'hooks/webapi';
import styles from './UploadID.module.scss';
import {
  validateFileType,
  validateMaxSize,
  validateNonEmpty,
} from 'utils/validations';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import FileSelectEvent from 'hooks/context/EventCollector/events/FileSelectEvent';
import { useTranslation } from 'react-i18next';
import { logger } from 'utils/logger';

type Props = {
  onSuccess: () => void;
  secondDocRequired?: boolean;
  disabled?: boolean;
  buttonText?: string;
};

type FormValues = {
  frontId: Blob | null;
  backId: Blob | null;
  secondDoc: Blob | null;
};

export default function UploadID({
  buttonText,
  secondDocRequired = false,
  disabled,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const { uploadAttachment, sendAttachmentsToOCR } = useDocumentUpload();
  const [pending, setPending] = useState(false);
  const { track } = useEventCollector();

  const handleSubmit = useCallback(
    async (form: FormValues, formikBag: FormikHelpers<FormValues>) => {
      const formDataFront = new FormData();
      const formDataBack = new FormData();
      const formDataSecondDoc = new FormData();

      if (
        !form.frontId ||
        !form.backId ||
        (secondDocRequired && !form.secondDoc)
      ) {
        return;
      }

      formDataFront.append('type', 'idCard');
      formDataFront.append('file', form.frontId);

      formDataBack.append('type', 'idCard');
      formDataBack.append('file', form.backId);

      if (form.secondDoc) {
        formDataSecondDoc.append('type', 'otherDocument');
        formDataSecondDoc.append('file', form.secondDoc);
      }

      try {
        setPending(true);

        const frontUploadId = await uploadAttachment(formDataFront);
        const backUploadId = await uploadAttachment(formDataBack);

        const docsList = [
          {
            type: 'ID_FRONT',
            id: frontUploadId.id,
          },
          {
            type: 'ID_BACK',
            id: backUploadId.id,
          },
        ];

        if (secondDocRequired) {
          const secondDocUploadId = await uploadAttachment(formDataSecondDoc);
          docsList.push({
            type: 'ADDITIONAL_DOCUMENT',
            id: secondDocUploadId.id,
          });
        }

        await sendAttachmentsToOCR(docsList);

        if (onSuccess) {
          onSuccess();
        }
      } catch {
        formikBag.setStatus('error');
        formikBag.setValues({
          frontId: null,
          backId: null,
          secondDoc: null,
        });
      }
    },
    [uploadAttachment, sendAttachmentsToOCR, onSuccess],
  );

  const validate = useCallback(({ frontId, backId, secondDoc }: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    errors.frontId =
      validateNonEmpty(frontId) ??
      validateFileType(frontId) ??
      validateMaxSize(frontId);

    errors.backId =
      validateNonEmpty(backId) ??
      validateFileType(backId) ??
      validateMaxSize(backId);

    if (secondDocRequired) {
      errors.secondDoc =
        validateNonEmpty(secondDoc) ??
        validateFileType(secondDoc) ??
        validateMaxSize(secondDoc);
    }

    return omitBy(errors, isNil);
  }, []);

  const formik = useFormik<FormValues>({
    initialValues: {
      frontId: null,
      backId: null,
      secondDoc: null,
    },
    validate,
    onSubmit: handleSubmit,
  });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = event.target;
      const file = event.target.files ? event.target.files[0] : null;
      formik.setFieldTouched(name, true, true);
      formik.setFieldValue(name, file);
      track(
        new FileSelectEvent({
          description: `${name} file selected`,
        }),
      );

      // Size and format of every picked file, so uploads blocked by validation are visible in the logs
      if (file) {
        logger.info('[UploadID] file selected', {
          field: name,
          ...logger.formatter.describeUploadFile(file),
        });
      }
    },
    [formik, track],
  );

  const handleDelete = useCallback(
    (name: string) => {
      formik.setFieldValue(name, null);
    },
    [formik],
  );

  const submitBtnDisabled =
    disabled ||
    !formik.values.frontId ||
    !formik.values.backId ||
    (secondDocRequired && !formik.values.secondDoc);

  // Formik silently skips onSubmit when a picked file fails validation, so tell the user why nothing happened
  const hasRejectedFile =
    formik.submitCount > 0 &&
    (Object.keys(formik.errors) as (keyof FormValues)[]).some(
      (field) => formik.values[field],
    );

  let errorMessage: string | undefined;
  if (hasRejectedFile) {
    errorMessage = t('error:validation.document_rejected');
  } else if (formik.status === 'error') {
    errorMessage = t('error:validation.invalid_image');
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className={styles.form}
      data-test="uploadDocuments-uploadId"
    >
      <div className={styles.uploads}>
        {secondDocRequired && (
          <div data-test="uploadDocuments-title">
            {t('document_upload:id_title')}
          </div>
        )}
        <DocumentUpload
          name="frontId"
          className={styles.upload}
          type="ID_FRONT"
          onChange={handleChange}
          isTouched={formik.touched.frontId}
          error={formik.errors.frontId}
          value={formik.values.frontId}
          onDelete={handleDelete}
          dataTest="id-front"
        />

        <DocumentUpload
          name="backId"
          className={styles.upload}
          type="ID_BACK"
          onChange={handleChange}
          isTouched={formik.touched.backId}
          error={formik.errors.backId}
          value={formik.values.backId}
          onDelete={handleDelete}
          dataTest="id-back"
        />

        {secondDocRequired && (
          <div className={styles.sec_doc_upload}>
            <div data-test="uploadDocuments-title">
              {t('upload_id:second_doc_title')}
            </div>
            <div
              className={styles.second_doc_desc}
              data-test="uploadDocuments-desc"
            >
              <InfoIcon
                className={styles.second_doc_desc_icon}
                width="19px"
                data-test="uploadDocuments-info-icon"
              />
              {t('upload_id:second_doc_desc')}
            </div>
            <DocumentUpload
              name="secondDoc"
              className={styles.upload}
              customSubtitle={t('document_upload:id_front_subtitle')}
              customTitle={t('manual_upload_id:second_doc_title_card')}
              type="ADDITIONAL_DOCUMENT"
              onChange={handleChange}
              isTouched={formik.touched.secondDoc}
              error={formik.errors.secondDoc}
              value={formik.values.secondDoc}
              onDelete={handleDelete}
              dataTest="second-doc"
            />
          </div>
        )}
      </div>

      {errorMessage && (
        <div
          className={styles['error-wrapper']}
          data-test="uploadDocuments-error-wrapper"
        >
          <HazardIcon
            className={styles['error-icon']}
            data-test="uploadDocuments-error-icon"
          />
          <Typography
            className={styles.error}
            dataTest="uploadDocuments-error-message"
          >
            {errorMessage}
          </Typography>
        </div>
      )}

      <Button
        pending={pending}
        type="submit"
        className={styles.continue}
        disabled={submitBtnDisabled}
        data-test="uploadDocuments-submit"
      >
        {buttonText ?? t('common:continue')}
      </Button>
    </form>
  );
}
